<script lang="ts">
	import {
		Button,
		ButtonType,
		ButtonSize,
		Icon,
		Progress,
		Tooltip,
		Label
	} from '@repo/commons/components'
	import { onDestroy } from 'svelte'
	import type { IconName } from '$lib_commons/svg-icons/iconsMap.ts'
	import { scaleFade } from '@repo/commons/utils'
	import { fade } from 'svelte/transition'
	import { type FileStatusType, FileStatus } from '@repo/commons/models'

	interface StatusLabels {
		uploaded?: {
			label: string
			icon?: IconName
		}
		warnings?: {
			label: string
			icon?: IconName
		}
		errors?: {
			label: string
			icon?: IconName
		}
	}

	interface Props {
		file: File
		status: FileStatusType
		statusLabels?: StatusLabels
		removable?: boolean
		warnings?: { group: string; warnings: string[] }[]
		errors?: { group: string; errors: string[] }[]
		showProgress?: boolean
		icon?: IconName
		subtitle?: string
		progress?: number
		variant?: 'default' | 'wide'
		borderless?: boolean
		onRemove: () => void
		onRetry?: () => void
		/** Adjusts how slow or fast progress simulation runs.
		 *  e.g. 1 = normal, 1.5 = 50% slower, 0.7 = faster */
		simulationMultiplier?: number
	}

	let {
		file,
		status,
		onRemove,
		onRetry,
		warnings,
		errors,
		statusLabels,
		showProgress,
		icon = 'document',
		subtitle,
		progress = undefined,
		variant = 'default',
		borderless = false,
		removable = true,
		simulationMultiplier = 1
	}: Props = $props()

	const defaultStatusLabels: Required<StatusLabels> = {
		uploaded: {
			label: 'Uploaded',
			icon: 'success'
		},
		warnings: {
			label: 'Errors found',
			icon: 'warning'
		},
		errors: {
			label: 'Failed',
			icon: 'error'
		}
	}

	const uploadedLabel = $derived({
		...defaultStatusLabels.uploaded,
		...statusLabels?.uploaded
	})

	const warningsLabel = $derived({
		...defaultStatusLabels.warnings,
		...statusLabels?.warnings
	})

	const errorsLabel = $derived({
		...defaultStatusLabels.errors,
		...statusLabels?.errors
	})

	let internalProgress = $state(0)
	let progressInterval: number | null = null

	// Larger files = slower progress simulation
	// Uses logarithmic decay for smooth transition across file sizes
	function calculateProgressIncrement(fileSize: number, multiplier = 1): number {
		const fileSizeMB = fileSize / (1024 * 1024)
		// Formula: starts at ~6 for small files, decreases to ~0.5 for large files
		const baseIncrement = Math.max(0.5, 6 / (1 + Math.log10(1 + fileSizeMB)))
		return baseIncrement / multiplier
	}

	function calculateIntervalDuration(fileSize: number, multiplier = 1): number {
		const fileSizeMB = fileSize / (1024 * 1024)
		// Formula: starts at ~60ms for tiny files, scales up to ~300ms for large files
		const baseDuration = 60 + 240 * Math.min(1, fileSizeMB / 20)
		return baseDuration * multiplier
	}

	// Start progress simulation when status changes to uploading
	$effect(() => {
		if (status === FileStatus.UPLOADING) {
			// Reset progress
			internalProgress = 0

			// Clear any existing interval
			if (progressInterval) {
				clearInterval(progressInterval)
			}

			const increment = calculateProgressIncrement(file.size, simulationMultiplier)
			const duration = calculateIntervalDuration(file.size, simulationMultiplier)

			// Start simulating progress
			progressInterval = setInterval(() => {
				if (internalProgress < 90) {
					// Slow down as we approach 90%
					const slowdownFactor = internalProgress > 70 ? 0.5 : 1
					const actualIncrement = Math.max(1, increment * slowdownFactor)
					internalProgress = Math.min(90, internalProgress + actualIncrement)
				}
			}, duration)
		} else if (status === FileStatus.FINALIZING) {
			// Stop simulation
			if (progressInterval) {
				clearInterval(progressInterval)
				progressInterval = null
			}

			// Instantly fill bar
			internalProgress = 100
		} else if (status === FileStatus.UPLOADED) {
			// Stop simulation
			if (progressInterval) {
				clearInterval(progressInterval)
				progressInterval = null
			}
		} else if (status === FileStatus.ERROR || status === FileStatus.PENDING) {
			// Reset progress
			internalProgress = 0

			// Clear interval
			if (progressInterval) {
				clearInterval(progressInterval)
				progressInterval = null
			}
		}
	})

	onDestroy(() => {
		if (progressInterval) {
			clearInterval(progressInterval)
		}
	})

	function formatFileSize(bytes: number): string {
		if (bytes === 0) return '0 Bytes'
		const k = 1024
		const sizes = ['Bytes', 'KB', 'MB', 'GB']
		const i = Math.floor(Math.log(bytes) / Math.log(k))
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
	}

	const warningTooltipText = $derived(
		warnings
			?.map(({ group, warnings: groupWarnings }) => {
				const lines = groupWarnings.join('\n')
				return group ? `${group}:\n${groupWarnings.map((e) => `- ${e}`).join('\n')}` : lines
			})
			.join('\n\n') ?? ''
	)

	const errorTooltipText = $derived(
		errors
			?.map(({ group, errors: groupErrors }) => {
				const lines = groupErrors.join('\n')
				return group ? `${group}:\n${groupErrors.map((e) => `- ${e}`).join('\n')}` : lines
			})
			.join('\n\n') ?? ''
	)

	const showProgressBar = $derived(
		progress !== undefined
			? progress > 0 && progress < 100
			: status === FileStatus.UPLOADING || status === FileStatus.FINALIZING
	)

	const showUploadedLabel = $derived(
		progress !== undefined
			? progress >= 100 && !errors?.length && !warnings?.length
			: status === FileStatus.UPLOADED && !errors?.length && !warnings?.length
	)
</script>

<div class="file-item" class:borderless>
	<div class="file-info">
		<div
			class="file-icon"
			class:has-warnings={!showProgressBar && warnings?.length}
			class:has-errors={!showProgressBar && errors?.length}
		>
			<Icon name={icon} />
		</div>

		<div class="file-details" class:wide={variant === 'wide'}>
			<div class="file-name" class:wide={variant === 'wide'} title={file.name}>
				{file.name}
			</div>
			<div class="file-meta">
				{subtitle ?? formatFileSize(file.size)}
			</div>
		</div>

		{#if warnings?.length && !showProgressBar}
			<div class="status-block" transition:scaleFade={{ duration: 200 }}>
				<div class="errors">
					<div class="error-indicator-wrapper">
						<p class="error-indicator">
							<Tooltip
								text={warningTooltipText}
								position="top-left"
								minWidth="320px"
								tooltipSize="large"
								offset={12}
							>
								<Label
									variant="warning"
									label={warningsLabel.label}
									size="sm"
									icon={warningsLabel.icon}
									iconSize="sm"
								/>
							</Tooltip>
						</p>
					</div>
				</div>
			</div>
		{:else if errors?.length && !showProgressBar}
			<div class="status-block" transition:scaleFade={{ duration: 200 }}>
				<div class="errors">
					<div class="error-indicator-wrapper">
						<p class="error-indicator">
							<Tooltip
								text={errorTooltipText}
								position="top-left"
								minWidth="320px"
								tooltipSize="large"
								offset={12}
							>
								<Label
									variant="error"
									label={errorsLabel.label}
									size="sm"
									icon={errorsLabel.icon}
									iconSize="sm"
								/>
							</Tooltip>
						</p>
					</div>
				</div>
			</div>
			{#if onRetry}
				<Button
					buttonSize={ButtonSize.SMALL}
					buttonType={ButtonType.TERTIARY_INVERSE}
					buttonText=""
					iconProps={{ name: 'refreshThin', size: 'md', class: 'text-[#002C4D80]' }}
					rounded
					onclick={onRetry}
				/>
			{/if}
		{:else if showProgressBar}
			<div class="progress-wrapper status-block" transition:fade={{ duration: 200 }}>
				<Progress value={progress ?? internalProgress} {showProgress} />
			</div>
		{:else if showUploadedLabel}
			<div class="status-block" transition:scaleFade={{ duration: 200 }}>
				<Label
					variant="success"
					label={uploadedLabel.label}
					size="sm"
					icon={uploadedLabel.icon}
					iconSize="sm"
				/>
			</div>
		{/if}

		{#if removable}
			<Button
				buttonSize={ButtonSize.SMALL}
				buttonType={ButtonType.TERTIARY_INVERSE}
				buttonText=""
				iconProps={{ name: 'trash', size: 'md', class: 'text-[#002C4D80]' }}
				rounded
				onclick={onRemove}
				disabled={status === FileStatus.UPLOADING}
			/>
		{/if}
	</div>
</div>

<style>
	.file-item {
		@apply bg-white border-b border-[#003A661A] py-4;
	}

	.file-item.borderless {
		@apply border-none;
	}

	.file-info {
		@apply flex items-center w-full gap-3;
	}

	.file-icon {
		@apply flex items-center justify-center w-12 h-12 bg-[#00498011] text-[#002C4D80] rounded-full flex-shrink-0;
	}

	.file-icon.has-errors {
		@apply bg-[#E4485B0D] text-[#E4485BCC];
	}

	.file-icon.has-warnings {
		@apply bg-[#FFC02E0D] text-[#8F6C1A];
	}

	.file-details {
		@apply flex-1 min-w-32;
	}

	.file-details.wide {
		@apply min-w-64;
	}

	.file-name {
		@apply text-sm text-[#001626E5] truncate max-w-32;
	}

	.file-name.wide {
		@apply max-w-full;
	}

	.file-meta {
		@apply text-sm text-[#001D33B2] mt-1;
	}

	.error-indicator-wrapper {
		@apply flex justify-end items-center max-w-full mx-6 mr-0;
	}

	.error-indicator {
		@apply flex items-center gap-2 text-sm text-[#E4485BCC];
	}

	.progress-wrapper {
		@apply w-full max-w-full ml-6;
	}

	.status-block {
		@apply inline-flex items-center justify-end transition-all;
	}
</style>
